import { useMemo, useState } from "react";
import { ArrowRight, CheckCircle2, ChevronDown, ChevronUp, Rocket, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { parseRubricData, type ParsedDimension, type ChatcraftScores } from "@/lib/rubricParser";
import { aggregateParsedDimensionsToCategories, type CategoryScore } from "@/lib/rubricCategories";
import type { JourneyFlow } from "@/lib/promptJourneyReceipt";

type DimensionScoreValue = number | { score: number; rationale?: string };

interface PromptScoreSummaryBarProps {
  flow: JourneyFlow;
  improvedScore: number;
  originalScore?: number;
  currentScore?: number;
  dimensionScores?: Record<string, DimensionScoreValue>;
  quickWins?: string[];
  isChat?: boolean;
  chatcraftScoresJson?: Record<string, number>;
}

function MiniBar({ percent }: { percent: number }) {
  return (
    <div className="h-1.5 overflow-hidden rounded-full bg-muted/70">
      <div
        className="h-full rounded-full bg-success transition-all"
        style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
      />
    </div>
  );
}

function CategoryMetricRow({ category }: { category: CategoryScore }) {
  const percent = category.percentOfMax * 100;
  const isExcellent = percent >= 90;

  return (
    <div className="py-2.5 first:pt-0 last:pb-0">
      <div className="mb-1.5 flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-foreground">{category.label}</span>
        <span className="shrink-0 font-mono text-xs text-muted-foreground">
          {Math.round(category.score)} / {category.max}
        </span>
      </div>
      <MiniBar percent={percent} />
      <div className="mt-1.5 space-y-1">
        <p className="flex items-start gap-1.5 text-xs leading-snug text-muted-foreground">
          <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-success" />
          {category.doingWell}
        </p>
        <p className={`flex items-start gap-1.5 text-xs leading-snug ${isExcellent ? "text-success" : "text-muted-foreground"}`}>
          <ArrowRight className={`mt-0.5 h-3 w-3 shrink-0 ${isExcellent ? "text-success" : "text-primary"}`} />
          {category.improve}
        </p>
      </div>
    </div>
  );
}

function DimensionMetricRow({ dimension }: { dimension: ParsedDimension }) {
  const percent = (dimension.score / dimension.max) * 100;
  const isExcellent = percent >= 90;

  return (
    <div className="py-2.5 first:pt-0 last:pb-0">
      <div className="mb-1.5 flex items-center justify-between gap-3">
        <span className="text-sm font-medium text-foreground">{dimension.label}</span>
        <span className="shrink-0 font-mono text-xs text-muted-foreground">
          {dimension.score} / {dimension.max}
        </span>
      </div>
      <MiniBar percent={percent} />
      <div className="mt-1.5 space-y-1">
        <p className="flex items-start gap-1.5 text-xs leading-snug text-muted-foreground">
          <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-success" />
          {dimension.whatWorked}
        </p>
        <p className={`flex items-start gap-1.5 text-xs leading-snug ${isExcellent ? "text-success" : "text-muted-foreground"}`}>
          <ArrowRight className={`mt-0.5 h-3 w-3 shrink-0 ${isExcellent ? "text-success" : "text-primary"}`} />
          {isExcellent ? "Keep doing this." : dimension.improvement}
        </p>
      </div>
    </div>
  );
}

function ChatcraftRows({ chatcraft }: { chatcraft: ChatcraftScores }) {
  return (
    <div className="mb-3 rounded-md border border-border/50 bg-muted/20 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Chat-Specific Criteria
        </span>
        <span className="font-mono text-xs text-muted-foreground">{chatcraft.total}/30</span>
      </div>
      <div className="grid gap-2 text-xs text-muted-foreground sm:grid-cols-3">
        <span>Convergence: {chatcraft.convergence}/12</span>
        <span>Constraints: {chatcraft.constraintMastery}/10</span>
        <span>Hygiene: {chatcraft.sessionHygiene}/8</span>
      </div>
    </div>
  );
}

export function PromptScoreSummaryBar({
  flow,
  improvedScore,
  originalScore,
  currentScore,
  dimensionScores,
  quickWins,
  isChat = false,
  chatcraftScoresJson,
}: PromptScoreSummaryBarProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const displayScore = currentScore ?? improvedScore;
  const showComparison = flow !== "builder" && typeof originalScore === "number";
  const scorePercent = Math.max(0, Math.min(100, displayScore));

  const parsed = useMemo(
    () => parseRubricData("", displayScore, dimensionScores, chatcraftScoresJson),
    [displayScore, dimensionScores, chatcraftScoresJson]
  );
  const categories = useMemo(() => aggregateParsedDimensionsToCategories(parsed.dimensions), [parsed.dimensions]);

  return (
    <Card className="overflow-hidden border-primary/25 bg-card/90">
      <CardHeader className="space-y-3 pb-3">
        <button
          type="button"
          className="w-full text-left"
          onClick={() => setIsExpanded((value) => !value)}
          aria-expanded={isExpanded}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <Target className="h-4 w-4 text-primary" />
              Score Breakdown
            </div>
            <div className="flex items-center justify-between gap-4 sm:justify-end">
              <div className="flex items-baseline gap-2">
                {showComparison && (
                  <>
                    <span className="font-display text-2xl font-bold text-muted-foreground">{originalScore}</span>
                    <ArrowRight className="h-4 w-4 text-success" />
                  </>
                )}
                <span className="font-display text-3xl font-bold text-primary">{displayScore}</span>
                <span className="text-sm text-muted-foreground">/100</span>
              </div>
              <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
                {isExpanded ? "Hide Metrics" : "See Metrics"}
                {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              </span>
            </div>
          </div>
        </button>
        <div className="h-2 overflow-hidden rounded-full bg-muted/70">
          <div
            className="h-full rounded-full bg-success transition-all"
            style={{ width: `${scorePercent}%` }}
          />
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="grid gap-6 pt-0 md:grid-cols-[minmax(190px,0.8fr)_minmax(0,1.6fr)]">
          <div>
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
              <Rocket className="h-4 w-4 text-primary" />
              Quick Wins
            </div>
            {quickWins && quickWins.length > 0 ? (
              <ul className="space-y-2">
                {quickWins.map((win, index) => (
                  <li key={`${win}-${index}`} className="flex items-start gap-2 text-sm leading-relaxed text-muted-foreground">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    {win}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">No quick wins available for this prompt.</p>
            )}
          </div>

          <div>
            {isChat && parsed.chatcraft && <ChatcraftRows chatcraft={parsed.chatcraft} />}
            <div className="divide-y divide-border/40">
              {showAdvanced
                ? parsed.dimensions.map((dimension) => (
                    <DimensionMetricRow key={dimension.key} dimension={dimension} />
                  ))
                : categories.map((category) => (
                    <CategoryMetricRow key={category.key} category={category} />
                  ))}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="mt-3 w-full text-muted-foreground hover:text-foreground"
              onClick={() => setShowAdvanced((value) => !value)}
            >
              {showAdvanced ? (
                <>
                  <ChevronUp className="mr-1 h-4 w-4" />
                  Hide Advanced Metrics
                </>
              ) : (
                <>
                  <ChevronDown className="mr-1 h-4 w-4" />
                  Advanced Metrics
                </>
              )}
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
